import { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react'

type ColorMode = 'dark' | 'light'
const defaultColorMode = 'light'
const localStorageKey = 'tm-color-mode'

export type ColorModeValueFallback = {
  enabled: false
  colorMode: ColorMode
  isDark: boolean
  isLight: boolean
}

export type ColorModeValueEnabled = {
  enabled: true
  colorMode: ColorMode
  isDark: boolean
  isLight: boolean
  toggleColorMode: () => void
}

export type ColorModeValue = ColorModeValueFallback | ColorModeValueEnabled

export function useColorModeState(): ColorModeValueEnabled {
  const [colorMode, setColorMode] = useState<ColorMode>(() => {
    try {
      const savedColorMode = localStorage.getItem(localStorageKey)
      return savedColorMode === 'light' || savedColorMode === 'dark' ? savedColorMode : defaultColorMode
    } catch (error) {
      return defaultColorMode
    }
  })
  return useMemo(
    () => ({
      enabled: true,
      colorMode,
      isDark: colorMode === 'dark',
      isLight: colorMode === 'light',
      toggleColorMode: () =>
        setColorMode(prevColorMode => {
          const nextColorMode = prevColorMode === 'light' ? 'dark' : 'light'
          try {
            localStorage.setItem(localStorageKey, nextColorMode)
          } catch (error) {}

          return nextColorMode
        }),
    }),
    [colorMode]
  )
}

export const ColorModeContext = createContext<ColorModeValue>({
  enabled: false,
  colorMode: defaultColorMode,
  isDark: false,
  isLight: true,
})

export function ColorModeProvider({ children }: PropsWithChildren<{}>) {
  const value = useColorModeState()
  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>
}

export function useColorMode() {
  return useContext(ColorModeContext)
}
